import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOMeta from '../../components/SEOMeta';

type LinkData = {
  slug: string;
  audio_url: string;
  nome: string;
  titulo: string | null;
  imagem: string | null;
  letra: string | null;
};

function formatTime(s: number) {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

const slugify = (s: string) =>
  s.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'inspira-musica';

export default function OuvirSlugPage({ lockedPreview = false }: { lockedPreview?: boolean }) {
  const { slug } = useParams<{ slug: string }>();
  const noDl = lockedPreview || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('nodl') === '1');
  // Modo "ouça a música completa antes de comprar": esconde download, compartilhamento
  // e edição de capa — só o player da música inteira, pra leads que têm receio de pagar
  // sem ouvir o resultado final.
  const hideActions = lockedPreview;
  const [data, setData] = useState<LinkData | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [imgBroken, setImgBroken] = useState(false);
  const [vinylHovered, setVinylHovered] = useState(false);
  const [showVinylBtn, setShowVinylBtn] = useState(true);
  const [copied, setCopied] = useState(false);
  const [trocandoFoto, setTrocandoFoto] = useState(false);
  const [fotoAtualizadaRecentemente, setFotoAtualizadaRecentemente] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!slug) return;
    fetch(`/api/buscar-link?slug=${encodeURIComponent(slug)}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setNotFound(true);
        else setData(d);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  // O player é sempre escuro. Força o chrome do Safari iOS (status bar / toolbar)
  // e o fundo de overscroll a ficarem escuros, independente do tema do sistema.
  // Restaura tudo ao sair da página.
  useEffect(() => {
    const DARK = '#08020e';
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlBg = html.style.backgroundColor;
    const prevBodyBg = body.style.backgroundColor;
    html.style.backgroundColor = DARK;
    body.style.backgroundColor = DARK;

    let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    const createdMeta = !meta;
    const prevThemeColor = meta ? meta.getAttribute('content') : null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', DARK);

    return () => {
      html.style.backgroundColor = prevHtmlBg;
      body.style.backgroundColor = prevBodyBg;
      if (createdMeta) meta?.remove();
      else if (prevThemeColor !== null) meta?.setAttribute('content', prevThemeColor);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => setPlaying(false);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [data]);

  useEffect(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (playing && !vinylHovered) {
      hideTimerRef.current = setTimeout(() => setShowVinylBtn(false), 1500);
    } else {
      setShowVinylBtn(true);
    }
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [playing, vinylHovered]);

  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 3500);
    return () => clearTimeout(t);
  }, [feedback]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); setPlaying(false); }
    else { audio.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const seekByClientX = (clientX: number) => {
    const audio = audioRef.current;
    const bar = progressRef.current;
    if (!audio || !bar || !audio.duration) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    audio.currentTime = pct * audio.duration;
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    seekByClientX(e.clientX);
    const onMove = (ev: MouseEvent) => seekByClientX(ev.clientX);
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const copiarLink = async () => {
    if (!slug) return;
    const linkPublico = `https://www.inspiramusicas.com.br/ouvir/${slug}`;
    try {
      await navigator.clipboard.writeText(linkPublico);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setFeedback({ tipo: 'erro', texto: 'Não consegui copiar. Selecione e copie manualmente.' });
    }
  };

  const compartilhar = async () => {
    if (!slug || !data) return;
    const linkShare = `https://www.inspiramusicas.com.br/share/${slug}`;
    const titulo = data.titulo || `Música para ${data.nome}`;
    const nav = navigator as Navigator & {
      share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
    };
    if (nav.share) {
      try {
        await nav.share({
          title: titulo,
          text: `Uma música feita especialmente para ${data.nome} 💛`,
          url: linkShare,
        });
      } catch { /* usuário cancelou */ }
    } else {
      try {
        await navigator.clipboard.writeText(linkShare);
        setFeedback({ tipo: 'ok', texto: 'Link copiado! Pode colar onde quiser compartilhar.' });
      } catch {
        setFeedback({ tipo: 'erro', texto: 'Não consegui copiar o link.' });
      }
    }
  };

  const compartilharFacebook = () => {
    if (!slug) return;
    const linkShare = `https://www.inspiramusicas.com.br/share/${slug}`;
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(linkShare)}`;
    window.open(url, '_blank', 'width=600,height=480,noopener,noreferrer');
  };

  const openPicker = () => fileInputRef.current?.click();

  const handleFotoSelecionada = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !slug) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setFeedback({ tipo: 'erro', texto: 'Use uma foto JPG, PNG ou WebP.' });
      event.target.value = '';
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setFeedback({ tipo: 'erro', texto: 'Foto muito grande. Máximo 4MB.' });
      event.target.value = '';
      return;
    }

    setTrocandoFoto(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = () => reject(new Error('read fail'));
        reader.readAsDataURL(file);
      });

      const response = await fetch('/api/upload-foto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, foto_base64: base64, mime_type: file.type }),
      });
      const json = await response.json();

      if (!response.ok || !json.foto_url) {
        setFeedback({ tipo: 'erro', texto: json.error || 'Erro ao enviar a foto. Tente de novo.' });
        return;
      }

      const novaUrl = `${json.foto_url}?t=${Date.now()}`;
      setData(prev => prev ? { ...prev, imagem: novaUrl } : prev);
      setImgBroken(false);
      setFotoAtualizadaRecentemente(true);
      setFeedback({ tipo: 'ok', texto: 'Capa atualizada! Quem ouvir agora vai ver essa foto 💛' });
    } catch {
      setFeedback({ tipo: 'erro', texto: 'Erro ao enviar foto. Tente novamente.' });
    } finally {
      setTrocandoFoto(false);
      event.target.value = '';
    }
  };

  if (notFound) return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <p style={{ color: 'var(--text-muted)', fontFamily: "'DM Sans', sans-serif" }}>Link inválido ou expirado.</p>
    </div>
  );

  if (!data) return (
    <div style={{ background: '#0e0518', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontFamily: "'DM Sans', sans-serif" }}>Carregando...</p>
    </div>
  );

  const titulo = data.titulo || `Música para ${data.nome}`;
  const shareUrl = `https://www.inspiramusicas.com.br/share/${slug}`;
  const shareText = encodeURIComponent(
    `🎵 *${titulo}*\n\nEssa música foi criada exclusivamente para *${data.nome}*. 🧡\n\n🎧 Ouça agora:\n${shareUrl}\n\nFeito pela Inspira Músicas — músicas personalizadas com história.\n🌐 inspiramusicas.com.br`
  );
  const downloadHref = `/api/img?url=${encodeURIComponent(data.audio_url)}&dl=1&nome=${encodeURIComponent(`inspira-musica-para-${slugify(data.nome)}`)}`;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const hasImage = Boolean(data.imagem && !imgBroken);

  const iconBtnStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    minHeight: 44,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.13)',
    color: 'rgba(255,255,255,0.92)',
    borderRadius: 999,
    padding: '11px 8px',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    textDecoration: 'none',
    transition: 'background 0.15s, border-color 0.15s, transform 0.15s',
  };

  return (
    <>
      <SEOMeta title={`${titulo} | Inspira Músicas`} description={`Ouça a música personalizada: ${titulo}`} path={`/${lockedPreview ? 'musica-completa' : 'ouvir'}/${slug}`} noIndex />

      <style>{`
        @keyframes vinylSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
          50%       { opacity: 1;   transform: translate(-50%, -50%) scale(1.08); }
        }
        @keyframes fadeRise {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ouvir-icon-btn:hover { background: rgba(255,255,255,0.12) !important; border-color: rgba(255,255,255,0.22) !important; }
        .ouvir-download-btn:hover { background: var(--gold-hover, #e07a38) !important; box-shadow: 0 4px 24px rgba(255,145,77,0.55) !important; transform: translateY(-1px); }
        .ouvir-photo-edit:hover { background: rgba(0,0,0,0.78) !important; transform: scale(1.06); }
        .ouvir-photo-empty:hover:not(:disabled) {
          border-color: rgba(255,145,77,0.55) !important;
          background: radial-gradient(circle at 50% 35%, rgba(255,145,77,0.14) 0%, transparent 60%), rgba(255,255,255,0.04) !important;
          transform: translateY(-2px);
        }
        .ouvir-card { animation: fadeRise 0.7s ease both; }
      `}</style>

      {/* Atmospheric blurred background */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden' }}>
        {hasImage ? (
          <img
            src={data.imagem!}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(48px) brightness(0.30) saturate(1.55)', transform: 'scale(1.15)' }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background:
              'radial-gradient(circle at 25% 20%, rgba(140,60,160,0.45) 0%, transparent 55%),' +
              'radial-gradient(circle at 75% 75%, rgba(255,145,77,0.18) 0%, transparent 55%),' +
              'linear-gradient(160deg, #0e0518 0%, #1a0a26 50%, #08020e 100%)',
          }} />
        )}
        {/* Vinheta escura por cima do blur — impede que as bordas claras da foto "lavem" as laterais */}
        <div style={{
          position: 'absolute', inset: 0,
          background:
            'radial-gradient(circle at 50% 32%, transparent 28%, rgba(8,2,14,0.58) 100%),' +
            'linear-gradient(180deg, rgba(8,2,14,0.50) 0%, transparent 22%, transparent 58%, rgba(8,2,14,0.82) 100%)',
        }} />
      </div>

      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
        <header style={{ padding: '16px 24px', textAlign: 'center' }}>
          <Link to="/">
            <img src="/logo-dark.png" alt="Inspira Músicas" style={{ height: 52 }} />
          </Link>
        </header>

        <main style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '8px 16px 64px' }}>
          <article className="ouvir-card" style={{
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(28px)',
            WebkitBackdropFilter: 'blur(28px)',
            border: '1px solid rgba(255,255,255,0.09)',
            borderRadius: 24,
            padding: '28px 24px 32px',
            maxWidth: 400,
            width: '100%',
            boxShadow: '0 16px 72px rgba(0,0,0,0.65)',
          }}>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFotoSelecionada}
              style={{ display: 'none' }}
            />

            {/* PHOTO SLOT — rectangular, original aspect ratio */}
            {hasImage ? (
              <figure style={{
                position: 'relative',
                width: '100%',
                maxWidth: 320,
                margin: '0 auto',
                borderRadius: 18,
                overflow: 'hidden',
                background: 'rgba(0,0,0,0.4)',
                boxShadow: '0 22px 56px -12px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06) inset',
              }}>
                <img
                  src={data.imagem!}
                  alt={`Foto de ${data.nome}`}
                  onError={() => setImgBroken(true)}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    maxHeight: 360,
                    objectFit: 'cover',
                  }}
                />
                {/* Edit affordance */}
                {!hideActions && (
                <button
                  onClick={openPicker}
                  disabled={trocandoFoto}
                  aria-label="Trocar foto"
                  className="ouvir-photo-edit"
                  style={{
                    position: 'absolute', top: 12, right: 12,
                    width: 38, height: 38,
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.55)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    cursor: trocandoFoto ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.2s, transform 0.2s',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                </button>
                )}
                {trocandoFoto && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(0,0,0,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: 13, fontWeight: 600, letterSpacing: '0.04em',
                  }}>
                    Enviando foto...
                  </div>
                )}
              </figure>
            ) : hideActions ? null : (
              <button
                className="ouvir-photo-empty"
                onClick={openPicker}
                disabled={trocandoFoto}
                style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 12,
                  width: '100%',
                  maxWidth: 320,
                  margin: '0 auto',
                  aspectRatio: '4 / 5',
                  border: '2px dashed rgba(255,255,255,0.18)',
                  borderRadius: 18,
                  background: 'radial-gradient(circle at 50% 35%, rgba(255,145,77,0.08) 0%, transparent 60%), rgba(255,255,255,0.025)',
                  color: 'rgba(255,255,255,0.7)',
                  cursor: trocandoFoto ? 'wait' : 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                  padding: 24,
                  transition: 'border-color 0.25s, transform 0.25s, background 0.25s',
                }}
              >
                {trocandoFoto ? (
                  <>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.18)', borderTopColor: 'rgba(255,255,255,0.85)', animation: 'vinylSpin 0.9s linear infinite' }} />
                    <span style={{ fontSize: 13, marginTop: 4 }}>Enviando foto...</span>
                  </>
                ) : (
                  <>
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    <span style={{ fontSize: 14.5, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '-0.01em', textAlign: 'center' }}>
                      Adicionar foto de {data.nome}
                    </span>
                    <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.42)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      Toque para enviar
                    </span>
                  </>
                )}
              </button>
            )}

            {/* VINYL — always abstract, smaller as secondary element */}
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginTop: 22, marginBottom: 18, height: 140 }}>
              {/* Glow halo */}
              <div style={{
                position: 'absolute',
                width: 200, height: 200,
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,145,77,0.55) 0%, rgba(196,90,140,0.28) 45%, transparent 75%)',
                filter: 'blur(10px)',
                animationName: 'glowPulse',
                animationDuration: '2.4s',
                animationTimingFunction: 'ease-in-out',
                animationIterationCount: 'infinite',
                animationPlayState: playing ? 'running' : 'paused',
                opacity: playing ? undefined : 0,
                transition: 'opacity 0.7s ease',
                pointerEvents: 'none',
              }} />

              {/* Rotating disc */}
              <div
                onClick={togglePlay}
                onMouseEnter={() => setVinylHovered(true)}
                onMouseLeave={() => setVinylHovered(false)}
                style={{
                  position: 'relative',
                  width: 140, height: 140,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  background:
                    'radial-gradient(circle at 28% 22%, rgba(255,210,170,0.92) 0%, rgba(255,160,100,0.4) 18%, transparent 38%),' +
                    'conic-gradient(from 45deg, #2d0f48, #5b1f78, #b04f8a, #ff914d, #c45a8c, #5b1f78, #2d0f48)',
                  animationName: 'vinylSpin',
                  animationDuration: '10s',
                  animationTimingFunction: 'linear',
                  animationIterationCount: 'infinite',
                  animationPlayState: playing ? 'running' : 'paused',
                  boxShadow: playing
                    ? '0 0 32px 6px rgba(255,145,77,0.42), 0 14px 38px rgba(0,0,0,0.65)'
                    : '0 8px 28px rgba(0,0,0,0.55)',
                  transition: 'box-shadow 0.6s ease',
                }}
              >
                {/* Vinyl groove rings (subtle) */}
                <div style={{
                  position: 'absolute', inset: 8, borderRadius: '50%',
                  background: 'repeating-radial-gradient(circle, rgba(0,0,0,0.10) 0, rgba(0,0,0,0.10) 1px, transparent 1px, transparent 4px)',
                  mixBlendMode: 'overlay', pointerEvents: 'none',
                }} />

                {/* Center hole + play/pause icon */}
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 44, height: 44, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.72)',
                  backdropFilter: 'blur(4px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)',
                  opacity: showVinylBtn ? 1 : 0,
                  transition: 'opacity 0.4s ease',
                  pointerEvents: showVinylBtn ? 'auto' : 'none',
                }}>
                  {playing
                    ? <svg width="14" height="14" viewBox="0 0 32 32" fill="none"><rect x="9" y="6" width="4" height="20" rx="2" fill="white" /><rect x="19" y="6" width="4" height="20" rx="2" fill="white" /></svg>
                    : <svg width="14" height="14" viewBox="0 0 32 32" fill="none"><path d="M12 7L26 16L12 25V7Z" fill="white" /></svg>
                  }
                </div>
              </div>
            </div>

            {/* TITLE BLOCK — editorial hierarchy */}
            <div style={{ textAlign: 'center', marginBottom: 22 }}>
              <p style={{
                fontSize: 10.5, fontWeight: 500,
                color: 'rgba(255,255,255,0.45)',
                textTransform: 'uppercase',
                letterSpacing: '0.26em',
                margin: '0 0 8px',
              }}>Música para</p>
              <h1 style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 800,
                fontSize: 26,
                letterSpacing: '-0.025em',
                color: 'white',
                margin: '0 0 10px',
                lineHeight: 1.18,
                textShadow: '0 2px 20px rgba(0,0,0,0.55)',
              }}>{data.nome}</h1>
              <p style={{
                color: 'rgba(255,255,255,0.38)',
                fontSize: 11.5,
                margin: 0,
                letterSpacing: '0.06em',
              }}>Música personalizada</p>
            </div>

            {/* Play + progress */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
              <button
                onClick={togglePlay}
                aria-label={playing ? 'Pausar' : 'Reproduzir'}
                style={{
                  flexShrink: 0,
                  width: 42, height: 42,
                  borderRadius: '50%',
                  background: 'var(--gold)',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 14px rgba(255,145,77,0.5)',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 4px 22px rgba(255,145,77,0.62)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 14px rgba(255,145,77,0.5)'; }}
              >
                {playing
                  ? <svg width="14" height="14" viewBox="0 0 32 32" fill="none"><rect x="9" y="6" width="4" height="20" rx="2" fill="white" /><rect x="19" y="6" width="4" height="20" rx="2" fill="white" /></svg>
                  : <svg width="14" height="14" viewBox="0 0 32 32" fill="none"><path d="M12 7L26 16L12 25V7Z" fill="white" /></svg>
                }
              </button>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  ref={progressRef}
                  onMouseDown={handleProgressMouseDown}
                  style={{
                    width: '100%', height: 5,
                    background: 'rgba(255,255,255,0.13)',
                    borderRadius: 999,
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div style={{
                    height: '100%',
                    width: `${progress}%`,
                    background: 'linear-gradient(90deg, #ff914d, #ffb07a)',
                    borderRadius: 999,
                    transition: 'width 0.25s linear',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7 }}>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontVariantNumeric: 'tabular-nums' }}>{formatTime(currentTime)}</span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontVariantNumeric: 'tabular-nums' }}>{formatTime(duration)}</span>
                </div>
              </div>
            </div>

            <audio
              ref={audioRef}
              src={data.audio_url}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              style={{ display: 'none' }}
            />

            <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)', margin: '8px 0 22px' }} />

            {/* Lyrics */}
            {data.letra && (
              <>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ fontWeight: 700, fontSize: 11, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Letra</p>
                  <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 12, padding: '18px 20px', border: '1px solid rgba(255,255,255,0.07)', maxHeight: 300, overflowY: 'auto' }}>
                    <pre style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.78)', lineHeight: 1.85, whiteSpace: 'pre-wrap', margin: 0 }}>{data.letra}</pre>
                  </div>
                </div>
                <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)', margin: '22px 0' }} />
              </>
            )}

            {/* Download (CTA primária) — escondida quando ?nodl=1 (entrega pré-pagamento) */}
            {!noDl && (
              <a
                href={downloadHref}
                className="ouvir-download-btn"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  background: 'var(--gold, #ff914d)', color: 'white', textDecoration: 'none',
                  borderRadius: 999, padding: '13px 28px',
                  fontSize: 15, fontWeight: 700, width: '100%', boxSizing: 'border-box',
                  boxShadow: '0 2px 14px rgba(255,145,77,0.45)',
                  transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s',
                  marginBottom: 12,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12" /><path d="m6 11 6 6 6-6" /><path d="M5 21h14" /></svg>
                Baixar música
              </a>
            )}

            {/* Compartilhamento — escondido no modo lockedPreview (pré-venda) */}
            {!hideActions && (<>
            {/* Linha de compartilhamento secundária */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button onClick={copiarLink} className="ouvir-icon-btn" style={iconBtnStyle} aria-label="Copiar link">
                {copied
                  ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg> Copiado</>
                  : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg> Copiar link</>
                }
              </button>

              <button onClick={compartilhar} className="ouvir-icon-btn" style={iconBtnStyle} aria-label="Compartilhar">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></svg>
                Enviar
              </button>

              <button onClick={compartilharFacebook} className="ouvir-icon-btn" style={iconBtnStyle} aria-label="Compartilhar no Facebook">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" /></svg>
                Facebook
              </button>
            </div>

            {/* WhatsApp share */}
            <a
              href={`https://api.whatsapp.com/send?text=${shareText}`}
              target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#25D366', color: 'white', textDecoration: 'none', borderRadius: 999, padding: '13px 28px', fontSize: 15, fontWeight: 700, width: '100%', boxSizing: 'border-box' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              Compartilhar no WhatsApp
            </a>
            </>)}

            {/* Aviso pós-troca de capa */}
            {fotoAtualizadaRecentemente && (
              <p style={{
                marginTop: 14,
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 12,
                color: 'rgba(255,255,255,0.7)',
                fontSize: 12,
                lineHeight: 1.55,
                textAlign: 'left',
              }}>
                💡 Se você já compartilhou o link antes, quem recebeu pode ver a capa antiga (WhatsApp e Facebook guardam por uns dias). Pra capa nova aparecer pra todos, compartilhe o link de novo agora.
              </p>
            )}

            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 18, lineHeight: 1.6, textAlign: 'center' }}>
              Feito com ♥ pela <a href="https://inspiramusicas.com.br" style={{ color: 'rgba(255,145,77,0.7)', textDecoration: 'none' }}>Inspira Músicas</a>
            </p>
          </article>
        </main>

        {/* Toast */}
        {feedback && (
          <div style={{
            position: 'fixed',
            bottom: 24, left: '50%', transform: 'translateX(-50%)',
            background: feedback.tipo === 'erro' ? 'rgba(180, 30, 30, 0.95)' : 'rgba(20, 30, 20, 0.95)',
            color: 'white',
            padding: '12px 22px',
            borderRadius: 999,
            fontSize: 13,
            fontFamily: "'DM Sans', sans-serif",
            border: feedback.tipo === 'erro' ? '1px solid rgba(255,120,120,0.4)' : '1px solid rgba(120,255,150,0.3)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            zIndex: 100,
            maxWidth: '90vw',
            textAlign: 'center',
          }}>
            {feedback.texto}
          </div>
        )}
      </div>
    </>
  );
}
